import SparkMD5 from "spark-md5";
import {
  cancelCreatorEpisodeChunks,
  initCreatorEpisodeChunks,
  mergeCreatorEpisodeChunks,
  uploadCreatorEpisodeChunk,
} from "@/api/member";

const CHUNK_SIZE = 5 * 1024 * 1024;
const CONCURRENCY = 2;
const MAX_RETRIES = 3;

export type CreatorUploadStage = "hashing" | "uploading" | "merging";

export interface CreatorUploadProgress {
  stage: CreatorUploadStage;
  percent: number;
}

export interface CreatorChunkUploadOptions {
  getToken: () => Promise<string | null>;
  onProgress?: (progress: CreatorUploadProgress) => void;
  signal?: AbortSignal;
}

function abortError() {
  return new DOMException("Upload cancelled", "AbortError");
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw abortError();
}

async function tokenOrThrow(getToken: CreatorChunkUploadOptions["getToken"]) {
  const token = await getToken();
  if (!token) throw new Error("unauthorized");
  return token;
}

function calculateFileMD5(file: File, onProgress?: CreatorChunkUploadOptions["onProgress"], signal?: AbortSignal) {
  return new Promise<string>((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer();
    const reader = new FileReader();
    const total = Math.ceil(file.size / CHUNK_SIZE);
    let index = 0;

    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    const onAbort = () => {
      reader.abort();
      cleanup();
      reject(abortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });

    const next = () => {
      if (signal?.aborted) return onAbort();
      const start = index * CHUNK_SIZE;
      reader.readAsArrayBuffer(file.slice(start, Math.min(start + CHUNK_SIZE, file.size)));
    };
    reader.onerror = () => {
      cleanup();
      reject(new Error("文件读取失败"));
    };
    reader.onload = () => {
      spark.append(reader.result as ArrayBuffer);
      index += 1;
      onProgress?.({ stage: "hashing", percent: Math.round((index / total) * 100) });
      if (index < total) {
        window.setTimeout(next, 0);
      } else {
        cleanup();
        resolve(spark.end());
      }
    };
    next();
  });
}

async function withRetry(task: () => Promise<void>, signal?: AbortSignal) {
  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
    throwIfAborted(signal);
    try {
      await task();
      return;
    } catch (error) {
      if (signal?.aborted) throw abortError();
      lastError = error;
      if (attempt < MAX_RETRIES) {
        await new Promise((resolve) => window.setTimeout(resolve, attempt * 800));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error("分片上传失败");
}

export async function uploadCreatorEpisodeInChunks(file: File, options: CreatorChunkUploadOptions) {
  throwIfAborted(options.signal);
  options.onProgress?.({ stage: "hashing", percent: 0 });
  const fileMd5 = await calculateFileMD5(file, options.onProgress, options.signal);
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const initParams = { fileMd5, fileName: file.name, fileSize: file.size, chunkSize: CHUNK_SIZE, totalChunks };
  const init = await initCreatorEpisodeChunks(initParams, await tokenOrThrow(options.getToken), options.signal);
	const uploadId = init.uploadId;
  const completed = new Set(init.uploadedChunks || []);
  const pending = Array.from({ length: totalChunks }, (_, index) => index + 1).filter((index) => !completed.has(index));
  let uploaded = completed.size;
  const workerController = new AbortController();
  const abortWorkers = () => workerController.abort();
  options.signal?.addEventListener("abort", abortWorkers, { once: true });
  const workerSignal = workerController.signal;

  options.onProgress?.({ stage: "uploading", percent: Math.round((uploaded / totalChunks) * 100) });
  try {
    const workers = Array.from({ length: Math.min(CONCURRENCY, pending.length) }, async () => {
    while (pending.length > 0) {
      throwIfAborted(workerSignal);
      const chunkIndex = pending.shift();
      if (!chunkIndex) return;
      const start = (chunkIndex - 1) * CHUNK_SIZE;
      const formData = new FormData();
      formData.append("file", file.slice(start, Math.min(start + CHUNK_SIZE, file.size)), file.name);
      formData.append("uploadId", init.uploadId);
      formData.append("chunkIndex", String(chunkIndex));
      formData.append("totalChunks", String(totalChunks));
      formData.append("fileMd5", fileMd5);
      formData.append("fileName", file.name);
      formData.append("fileSize", String(file.size));
      formData.append("chunkSize", String(CHUNK_SIZE));
      await withRetry(async () => {
        const token = await tokenOrThrow(options.getToken);
        await uploadCreatorEpisodeChunk(formData, token, workerSignal);
      }, workerSignal);
      uploaded += 1;
      options.onProgress?.({ stage: "uploading", percent: Math.round((uploaded / totalChunks) * 100) });
    }
		});
    await Promise.all(workers);

    throwIfAborted(options.signal);
    options.onProgress?.({ stage: "merging", percent: 100 });
    return await mergeCreatorEpisodeChunks(
      { uploadId, fileMd5, fileName: file.name, fileSize: file.size, totalChunks },
      await tokenOrThrow(options.getToken),
      options.signal,
    );
  } catch (error) {
    workerController.abort();
    if (options.signal?.aborted && uploadId) {
      try {
        await cancelCreatorEpisodeChunks(uploadId, await tokenOrThrow(options.getToken));
      } catch {
        // The next upload can still resume these chunks when cleanup is unavailable.
      }
    }
    throw error;
  } finally {
    options.signal?.removeEventListener("abort", abortWorkers);
  }
}
